import clsx from 'clsx';
import React, { useState } from 'react';

import useEventTracking from '@hooks/useEventTracking';
import {
  new_session_path,
  root_path,
  search_page_path,
  subscribe_path,
  subscribe_promotion_path,
  upgrade_subscription_path,
} from '@routes';
import { ClickTarget, EventProperties, TrackingLocation, click } from '@tracking';
import { NavCurrentUser } from '@utils/backend-api/currentUser';

import Button from '../Button';
import { PrimaryButton, ProButton } from '../Button/variants';
import IconClose from '../Images/Rebranding/IconClose';
import IconHamburger from '../Images/Rebranding/IconHamburger';
import IconTI from '../Images/Rebranding/IconTI';
import LogoTIProText from '../Images/Rebranding/LogoTIProText';
import LogoTIText from '../Images/Rebranding/LogoTIText';
import SearchIcon from '../Images/SearchIcon';
import SideNav from './SideNav';
import SettingsSubmenuNav from './SubmenuNav/SettingsSubmenuNav';
import SettingsSubmenuNavFooter from './SubmenuNav/SettingsSubmenuNav/Footer';
import SettingsSubmenuNavItem from './SubmenuNav/SettingsSubmenuNav/NavItem';
import TopNav from './TopNav';
import NavItemList from './TopNav/NavItemList';
import { SETTINGS_SUBNAV_MAIN_NAV_ITEMS } from './navItems';

type NavContentProps = {
  analytics?: EventProperties;
  className?: string;
  currentUser: NavCurrentUser;
  isScrollStyle?: boolean;
  overrideOfferCode?: string;
};

const NavContent = ({
  analytics,
  className,
  currentUser,
  isScrollStyle,
  overrideOfferCode,
}: NavContentProps) => {
  const showProLogo = currentUser.isProSubscriber || currentUser.willSubscriptionChange;
  const [isSideNavOpen, setIsSideNavOpen] = useState(false);
  const [isMenuClicked, setIsMenuClicked] = useState(false);
  const { Track, trackEvent } = useEventTracking({
    ...analytics,
    location: TrackingLocation.Nav,
  });

  const toggleSideNav = () => {
    setIsSideNavOpen(!isSideNavOpen);
    if (!isMenuClicked) {
      setIsMenuClicked(true);
    }
    trackEvent(
      click({
        clickTarget: ClickTarget.SideNavToggle,
        clickText: !isSideNavOpen ? 'open' : 'close',
      })
    );
  };

  return (
    <Track>
      <header id="global-nav" className={clsx(className, 'h-full sm:h-max')}>
        <div
          className={clsx('flex h-full items-center justify-center sm:h-[53px] md:h-64', {
            'flex sm:hidden': isScrollStyle,
          })}
        >
          <div className="flex w-28 flex-1 sm:hidden">
            <Button
              as="button"
              className={clsx('border-none bg-transparent p-0 outline-none', {
                'text-white': isScrollStyle,
                'text-black': !isScrollStyle,
              })}
              onClick={toggleSideNav}
            >
              <span
                className={clsx('flex', {
                  'animate-spin-once': isMenuClicked && isSideNavOpen,
                  'animate-spin-once-reverse': isMenuClicked && !isSideNavOpen,
                })}
              >
                {isSideNavOpen ? (
                  <IconClose className="w-20" />
                ) : (
                  <IconHamburger className="w-28" />
                )}
              </span>
            </Button>
          </div>
          <a
            href={root_path()}
            className={clsx('flex max-w-full flex-col justify-center sm:block', {
              'w-[190px] sm:w-[360px] md:w-[529px]': !showProLogo && !isScrollStyle,
              'w-[190px] sm:w-[390px] md:w-[586px]': showProLogo && !isScrollStyle,
            })}
            onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Logo }))}
          >
            {isScrollStyle ? (
              <IconTI className="w-24 text-primary-magenta" />
            ) : showProLogo ? (
              <>
                <LogoTIProText className="text-black sm:w-full" aria-hidden />
                <span className="sr-only">The Information Pro</span>
              </>
            ) : (
              <>
                <LogoTIText className="text-black sm:w-full" aria-hidden />
                <span className="sr-only">The Information</span>
              </>
            )}
          </a>
          <div className="relative z-10 flex flex-1 items-center justify-end gap-28">
            <a
              href={search_page_path()}
              className={clsx(
                'leading-[0] transition-colors hover:text-primary-magenta focus:text-primary-magenta',
                {
                  'block text-white': isScrollStyle,
                  'hidden text-black sm:block': !isScrollStyle,
                }
              )}
              onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.SearchButton }))}
            >
              <SearchIcon className="w-20" />
            </a>
            {currentUser.isSubscriber &&
              !currentUser.isProSubscriber &&
              !currentUser.willSubscriptionChange && (
                <ProButton
                  as="link"
                  className="hidden sm:block"
                  href={upgrade_subscription_path()}
                  onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Upgrade }))}
                >
                  Upgrade to Pro
                </ProButton>
              )}
            {currentUser.isLoggedIn && !currentUser.isSubscriber && (
              <PrimaryButton
                as="link"
                className="hidden sm:block"
                href={
                  overrideOfferCode
                    ? subscribe_promotion_path({ offer: overrideOfferCode })
                    : subscribe_path()
                }
                onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Subscribe }))}
              >
                Subscribe
              </PrimaryButton>
            )}
            {currentUser.isLoggedIn ? (
              <SettingsSubmenuNav
                as="button"
                avatarUrl={currentUser.avatarUrl}
                isProSubscriber={currentUser.isProSubscriber}
              >
                <NavItemList className="space-y-20">
                  {SETTINGS_SUBNAV_MAIN_NAV_ITEMS(currentUser.username).map(({ name, ...rest }) => (
                    <SettingsSubmenuNavItem
                      className="hover:text-primary-magenta focus:text-primary-magenta"
                      as="link"
                      key={name}
                      onClickCapture={() =>
                        trackEvent(
                          click({ clickTarget: ClickTarget.SettingsMenuItem, clickText: name })
                        )
                      }
                      {...rest}
                    >
                      {name}
                    </SettingsSubmenuNavItem>
                  ))}
                </NavItemList>
                <hr className="my-20" />
                <SettingsSubmenuNavFooter
                  avatarUrl={currentUser.avatarUrl}
                  profileCompletionPercentage={currentUser.profileCompletionPercentage}
                  profileCompletionStatuses={currentUser.profileCompletionStatuses}
                  username={currentUser.username}
                  initialSubscribeDateStr={currentUser.initialSubscribeDateStr}
                />
              </SettingsSubmenuNav>
            ) : (
              <>
                <Button
                  as="link"
                  className={clsx(
                    'font-suisse-intl-mono text-overline-md transition-colors hover:text-primary-magenta focus:text-primary-magenta',
                    {
                      'text-white': isScrollStyle,
                      'text-black': !isScrollStyle,
                    }
                  )}
                  href={new_session_path()}
                  onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.SignIn }))}
                >
                  Sign in
                </Button>
                <PrimaryButton
                  as="link"
                  className="hidden sm:block"
                  href={
                    overrideOfferCode
                      ? subscribe_promotion_path({ offer: overrideOfferCode })
                      : subscribe_path()
                  }
                  onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Subscribe }))}
                >
                  Subscribe
                </PrimaryButton>
              </>
            )}
          </div>
        </div>
        <TopNav currentUser={currentUser} isScrollStyle={isScrollStyle} />
        <SideNav
          currentUser={currentUser}
          isSideNavOpen={isSideNavOpen}
          setIsSideNavOpen={setIsSideNavOpen}
        />
      </header>
    </Track>
  );
};

export default NavContent;
